<script setup lang="ts">
/**
 * Step 1 du checkout événement :
 *  - places disponibles globales
 *  - liste des tarifs avec quantité + tarif libre + hint options
 *  - récap total + bouton Continuer
 */
import type { TicketType } from "~/types/event";

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

defineProps<{
  ticketTypes: TicketType[];
  selections: Record<string, number>;
  flexiblePrices: Record<string, number>;
  totalSelected: number;
  totalPrice: number;
  promoDiscount: number;
  totalRemainingPlaces: number | null;
  effectiveCapacity: number | null;
  animatedGlobalRemaining: number;
  getRemainingForDisplay: (tt: TicketType) => number | null;
  isSoldOut: (tt: TicketType) => boolean;
  getMaxForTicketType: (tt: TicketType) => number;
  getOptionsForTicketType: (id: string) => unknown[];
  flexibleSuggestions: (tt: TicketType) => number[];
}>();

const emit = defineEmits<{
  // 2e arg = max pour increment, montant pour update:flexible-price.
  (e: 'increment' | 'update:flexible-price', id: string, value: number): void;
  (e: 'decrement', id: string): void;
  (e: 'continue'): void;
}>();

/**
 * Réplique la sémantique du modifieur `.number` de `v-model` : `parseFloat`,
 * fallback 0 si non numérique (champ vidé). La mutation du tarif libre est
 * déléguée au parent (état propriétaire) plutôt que faite sur la prop.
 * Cf. vue/no-mutating-props.
 */
function onFlexiblePriceInput(id: string, event: Event) {
  const raw = (event.target as HTMLInputElement).value;
  const parsed = Number.parseFloat(raw);
  emit('update:flexible-price', id, Number.isNaN(parsed) ? 0 : parsed);
}
</script>

<template>
  <div>
    <div v-if="totalRemainingPlaces !== null" class="px-4 md:px-6 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
      <span class="text-xs text-[#717198] dark:text-gray-400">{{ t('billetterie.bslugStepTickets.placesDisponibles') }}</span>
      <span class="text-sm font-bold tabular-nums transition-colors duration-300" :class="totalRemainingPlaces <= 10 ? 'text-red-500' : 'text-[#2d2e5c] dark:text-white'">
        {{ animatedGlobalRemaining }} / {{ effectiveCapacity }}
      </span>
    </div>

    <div class="divide-y divide-gray-100 dark:divide-gray-800">
      <div
        v-for="tt in ticketTypes.filter(t => t.is_active)"
        :key="tt.id"
        class="px-4 md:px-6 py-4 md:py-5 transition-opacity"
        :class="isSoldOut(tt) ? 'opacity-60' : ''"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ tt.name }}</p>
              <span
                v-if="isSoldOut(tt)"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400"
              >
                {{ t('billetterie.partage.complet') }}
              </span>
            </div>
            <p v-if="tt.description" class="text-xs text-[#717198] dark:text-gray-400 mt-0.5">{{ tt.description }}</p>
            <p v-if="tt.pricing === 'flexible' && !isSoldOut(tt)" class="text-xs text-[#717198] dark:text-gray-400 mt-0.5 italic">
              {{ t('billetterie.bslugStepTickets.choisissezMontantSoutien') }}
            </p>
            <div v-if="!isSoldOut(tt)" class="flex items-center gap-2 mt-1">
              <span v-if="getRemainingForDisplay(tt) !== null" class="text-[11px] tabular-nums transition-colors duration-300" :class="getRemainingForDisplay(tt)! <= 10 ? 'text-red-500 font-medium' : 'text-[#717198] dark:text-gray-500'">
                {{ t('billetterie.partage.placesRestantesTexte', getRemainingForDisplay(tt)!) }}
              </span>
              <span v-if="tt.max_per_order && tt.max_per_order < 10" class="text-[11px] text-[#717198] dark:text-gray-500">
                {{ t('billetterie.bslugStepTickets.maxParPersonne', { n: tt.max_per_order }) }}
              </span>
            </div>
            <div v-if="tt.pricing !== 'flexible'" class="mt-1.5 md:hidden">
              <span v-if="Number(tt.price) > 0" class="text-sm font-bold text-[#2d2e5c] dark:text-white">
                {{ formatCurrency(Number(tt.price)) }}
              </span>
              <span v-else class="text-sm font-bold text-emerald-600 dark:text-emerald-400">{{ t('billetterie.partage.gratuit') }}</span>
            </div>
          </div>
          <div v-if="tt.pricing !== 'flexible'" class="hidden md:block w-[140px] text-right shrink-0 mr-4">
            <template v-if="Number(tt.price) > 0">
              <span class="text-base font-bold text-[#2d2e5c] dark:text-white">{{ formatCurrency(Number(tt.price)) }}</span>
            </template>
            <span v-else class="text-base font-bold text-emerald-600 dark:text-emerald-400">{{ t('billetterie.partage.gratuit') }}</span>
          </div>
          <!-- Tarif complet : on remplace les steppers par un libellé clair
               (impossible de sélectionner). Sinon, les steppers habituels. -->
          <div
            v-if="isSoldOut(tt)"
            :data-testid="`ticket-soldout-${tt.id}`"
            class="shrink-0 text-xs font-semibold text-red-500 dark:text-red-400"
          >
            {{ t('billetterie.partage.epuise') }}
          </div>
          <div v-else class="flex items-center gap-1.5 md:gap-2 shrink-0">
            <button
              :data-testid="`ticket-decrement-${tt.id}`"
              :aria-label="t('billetterie.partage.diminuerQuantiteDe', { nom: tt.name })"
              class="w-8 h-8 rounded-xl flex items-center justify-center transition-colors disabled:opacity-30"
              :style="selections[tt.id] ? { background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' } : {}"
              :class="!selections[tt.id] ? 'border border-gray-300 dark:border-gray-600 text-gray-500' : ''"
              :disabled="!selections[tt.id]"
              @click="$emit('decrement', tt.id)"
            >
              <UIcon name="i-lucide-minus" class="size-3.5" />
            </button>
            <span :data-testid="`ticket-quantity-${tt.id}`" class="w-6 text-center text-sm font-bold text-[#2d2e5c] dark:text-white">{{ selections[tt.id] || 0 }}</span>
            <button
              :data-testid="`ticket-increment-${tt.id}`"
              :aria-label="t('billetterie.partage.augmenterQuantiteDe', { nom: tt.name })"
              class="w-8 h-8 rounded-xl flex items-center justify-center transition-colors disabled:opacity-30"
              :style="(selections[tt.id] || 0) < getMaxForTicketType(tt) ? { background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' } : {}"
              :class="(selections[tt.id] || 0) >= getMaxForTicketType(tt) ? 'border border-gray-300 dark:border-gray-600 text-gray-500' : ''"
              :disabled="(selections[tt.id] || 0) >= getMaxForTicketType(tt)"
              @click="$emit('increment', tt.id, getMaxForTicketType(tt))"
            >
              <UIcon name="i-lucide-plus" class="size-3.5" />
            </button>
          </div>
        </div>

        <Transition
          enter-active-class="transition-all duration-300 ease-out overflow-hidden"
          enter-from-class="opacity-0 max-h-0 mt-0"
          enter-to-class="opacity-100 max-h-32 mt-3"
          leave-active-class="transition-all duration-200 ease-in overflow-hidden"
          leave-from-class="opacity-100 max-h-32 mt-3"
          leave-to-class="opacity-0 max-h-0 mt-0"
        >
          <div
            v-if="tt.pricing === 'flexible' && (selections[tt.id] || 0) > 0"
            class="rounded-2xl border border-gray-200 dark:border-[#262626] bg-gray-50 dark:bg-[#0f0f0f] p-3"
          >
            <label class="block text-xs font-semibold text-[#2d2e5c] dark:text-white mb-2">
              {{ t('billetterie.bslugStepTickets.choisissezVotreMontant') }}
            </label>
            <div class="flex items-center gap-2">
              <div class="relative flex-1 max-w-[200px]">
                <input
                  :value="flexiblePrices[tt.id]"
                  type="number"
                  inputmode="decimal"
                  :min="tt.min_price || 0"
                  :max="tt.max_price || undefined"
                  step="0.01"
                  :placeholder="t('billetterie.bslugStepTickets.placeholderMontantLibre')"
                  class="w-full h-11 pl-3 pr-10 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] text-base font-semibold text-[#2d2e5c] dark:text-white focus:outline-none focus:border-gray-400 dark:focus:border-gray-500 tabular-nums transition-colors"
                  @input="onFlexiblePriceInput(tt.id, $event)"
                />
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-base font-medium text-gray-400 pointer-events-none">€</span>
              </div>
              <div v-if="(tt.min_price ?? 0) > 0" class="flex items-center gap-1.5">
                <button
                  v-for="amount in flexibleSuggestions(tt)"
                  :key="amount"
                  class="px-2.5 h-8 rounded-lg text-xs font-semibold border transition-all"
                  :class="flexiblePrices[tt.id] === amount ? 'border-[#212121] dark:border-white bg-[#212121] dark:bg-white text-white dark:text-[#212121]' : 'border-gray-300 dark:border-[#333] text-[#505870] dark:text-gray-400 hover:border-gray-400 hover:bg-white dark:hover:bg-[#141414]'"
                  type="button"
                  @click="emit('update:flexible-price', tt.id, amount)"
                >
                  {{ formatCurrency(amount) }}
                </button>
              </div>
            </div>
            <p class="text-[11px] text-[#717198] dark:text-gray-500 mt-2 flex items-center gap-1">
              <UIcon name="i-lucide-info" class="size-3" />
              <span v-if="(tt.min_price ?? 0) > 0 && (tt.max_price ?? 0) > 0">
                {{ t('billetterie.bslugStepTickets.entreMinEtMax', { min: formatCurrency(Number(tt.min_price)), max: formatCurrency(Number(tt.max_price)) }) }}
              </span>
              <span v-else-if="(tt.min_price ?? 0) > 0">
                {{ t('billetterie.bslugStepTickets.minimumMontant', { min: formatCurrency(Number(tt.min_price)) }) }}
              </span>
              <span v-else-if="(tt.max_price ?? 0) > 0">
                {{ t('billetterie.bslugStepTickets.maximumMontant', { max: formatCurrency(Number(tt.max_price)) }) }}
              </span>
              <span v-else>{{ t('billetterie.bslugStepTickets.montantLibreOrganisateur') }}</span>
            </p>
          </div>
        </Transition>

        <div v-if="getOptionsForTicketType(tt.id).length > 0" class="mt-2">
          <p class="text-[11px] text-[#717198] dark:text-gray-500 flex items-center gap-1">
            <UIcon name="i-lucide-plus-circle" class="size-3" />
            {{ t('billetterie.bslugStepTickets.optionsEtapeSuivante') }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between px-4 md:px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-[#0f0f0f]">
      <div>
        <span v-if="promoDiscount > 0" class="text-xs text-gray-400 line-through mr-2">{{ formatCurrency(totalPrice + promoDiscount) }}</span>
        <span class="font-bold text-base md:text-lg text-[#2d2e5c] dark:text-white">{{ formatCurrency(totalPrice) }}</span>
      </div>
      <button data-testid="btn-step1-continue" class="px-6 md:px-8 py-2.5 md:py-3 accent-btn rounded-xl text-sm font-semibold transition-colors disabled:opacity-40" :disabled="totalSelected === 0" @click="$emit('continue')">
        {{ t('commun.actions.continuer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
